import { Observable } from 'rxjs/Observable'

import { IFormMapping, IFormBasePropertyMapping, IFormOption } from './form-mappings'
import {
  Form,
  ControlTypes,
  MultiRelationshipPrompt,
  BasePrompt,
  SingleRelationshipPrompt,
  DropdownPrompt,
  RadioPrompt
} from '../../models'
import { BaseModel } from '../../../data/models'
import { Utils, FormHelper } from '../../../helpers'
import { PromptFactory } from '../../models/prompt/prompt-factory'

type KeyIdentifier = (model: BaseModel) => string

export class FormMapper {

  private _mappingRegistry = new Map<string, IFormMapping>()
  private _formStrategies = new Map<ControlTypes, (model, propertyMapping) => void>()
  private _modelStrategies = new Map<ControlTypes, (model, propertyMapping, formData) => Observable<BaseModel>>()
  private _keyIdentifier: KeyIdentifier

  public constructor(
    formMappings: IFormMapping[],
    private _promptFactory: PromptFactory,
    private _utils: Utils,
    private _formHelper: FormHelper,
    _keyIdentifier?: KeyIdentifier
  ) {
    this._keyIdentifier = _keyIdentifier || this._defaultKeyIdentifier
    this._registerMappings(formMappings)

    this._formStrategies.set(ControlTypes.MultiRelationship, this._multiRelationshipFormStrategy)
    this._formStrategies.set(ControlTypes.SingleRelationship, this._singleRelationshipFormStrategy)
    this._formStrategies.set(ControlTypes.Dropdown, this._dropDownFormStrategy)
    this._formStrategies.set(ControlTypes.Radio, this._radioFormStrategy)

    this._modelStrategies.set(ControlTypes.MultiRelationship, this._multiModelStrategy)
    this._modelStrategies.set(ControlTypes.SingleRelationship, this._singleModelStrategy)
    this._modelStrategies.set(ControlTypes.Dropdown, this._optionsCallbackModelStrategy)
    this._modelStrategies.set(ControlTypes.Radio, this._optionsCallbackModelStrategy)
    this._modelStrategies.set(ControlTypes.MultiSelect, this._optionsCallbackModelStrategy)
    this._modelStrategies.set(ControlTypes.Label, this._nullModelStrategy)
  }

  public toForm<T extends BaseModel>(model: T): Form<T> {
    const form = new Form<T>(model)
    const key = this._keyIdentifier(model)
    const mapping = this._mappingRegistry.get(key)
    let prompt

    if (! mapping) {
      throw new Error(`No form mapping registered for "${key}"`)
    }

    form.title = mapping.title

    mapping.properties.forEach((propertyMapping) => {
      propertyMapping = this._mergeDefaultPropertyMapping(propertyMapping)
      prompt = this._runFormStrategy(model, propertyMapping)
      form.prompts.push(prompt)
    })

    return form
  }

  public toModel(model: BaseModel, formData: {}): Observable<BaseModel> {
    const key = this._keyIdentifier(model)
    const mapping = this._mappingRegistry.get(key)
    const observables: Array<Observable<BaseModel>> = []
    mapping.properties.forEach((propertyMapping) => {
      observables.push(this._runModelStrategy(model, propertyMapping, formData))
    })
    return Observable.forkJoin(...observables).map(() => model)
  }

  private _registerMappings(formMappings: IFormMapping[]) {
    formMappings.forEach((formMapping) => {
      this._mappingRegistry.set(formMapping.classType, formMapping)
    })
  }

  private _runFormStrategy(model: BaseModel, propertyMapping: IFormBasePropertyMapping) {
    const strategy = this._formStrategies.get(propertyMapping.controlType) || this._defaultFormStrategy
    return strategy.call(this, model, propertyMapping)
  }

  private _runModelStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping,
    formData: {}
  ): Observable<BaseModel> {
    const strategy = this._modelStrategies.get(propertyMapping.controlType) || this._defaultModelStrategy
    return strategy.call(this, model, propertyMapping, formData)
  }

  private _multiRelationshipFormStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping
  ): MultiRelationshipPrompt<BaseModel> {
    const prompt = this._baseRelationshipMappingStrategy(model, propertyMapping) as MultiRelationshipPrompt<BaseModel>

    prompt.forms = model[propertyMapping.key]
      .map((subModel) => this.toForm(subModel))
    return prompt
  }

  private _singleRelationshipFormStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping
  ): SingleRelationshipPrompt<BaseModel> {
    const prompt = this._baseRelationshipMappingStrategy(model, propertyMapping) as SingleRelationshipPrompt<BaseModel>
    prompt.form = this.toForm(model[propertyMapping.key])
    return prompt
  }

  private _baseRelationshipMappingStrategy(model: BaseModel, propertyMapping: IFormBasePropertyMapping): BasePrompt<any> {
    const filterSpec = {type: 'exclude', keys: ['promptMappings']}
    const allowedProperties = this._utils.filterKeys(propertyMapping, filterSpec) as IFormBasePropertyMapping

    return this._createPrompt(model, allowedProperties)
  }

  private _radioFormStrategy(model: BaseModel, propertyMapping: IFormBasePropertyMapping): RadioPrompt {
    const prompt = this._defaultFormStrategy(model, propertyMapping) as RadioPrompt
    prompt.options = propertyMapping.optionsCallback.toForm()
    return prompt
  }

  private _dropDownFormStrategy(model: BaseModel, propertyMapping: IFormBasePropertyMapping): DropdownPrompt {
    const prompt = this._defaultFormStrategy(model, propertyMapping) as DropdownPrompt
    prompt.options = this._getOptionsCallbackWithdefault(propertyMapping)
    return prompt
  }

  private _getOptionsCallbackWithdefault(
    propertyMapping: IFormBasePropertyMapping
  ): Observable<IFormOption[]> {
    return propertyMapping.optionsCallback.toForm().map((options) => {
      options = this._formHelper.addDefaultOption(options)
      return options
    })
  }

  private _defaultFormStrategy(model: BaseModel, propertyMapping: IFormBasePropertyMapping): BasePrompt<any> {
    return this._createPrompt(model, propertyMapping)
  }

  private _multiModelStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping,
    formData: {}
  ): Observable<BaseModel> {
    const observables: Array<Observable<BaseModel>> = [Observable.of({} as BaseModel)]
    model[propertyMapping.key].forEach((subModel, index) => {
      observables.push(
        this.toModel(subModel, formData[propertyMapping.key][index]).map((childModel) => {
          subModel = childModel
          return subModel
        })
      )
    })
    return Observable.forkJoin(...observables).map(() => model)
  }

  private _singleModelStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping,
    formData: {}
  ): Observable<BaseModel> {
    return this.toModel(model[propertyMapping.key], formData[propertyMapping.key]).map((childModel) => {
      return model[propertyMapping.key] = childModel
    })
  }

  private _optionsCallbackModelStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping,
    formData: {}
  ): Observable<BaseModel> {
    return propertyMapping.optionsCallback.toModel(formData[propertyMapping.key]).map((childModel) => {
      model[propertyMapping.key] = childModel || null
      return model
    })
  }

  private _defaultModelStrategy(
    model: BaseModel,
    propertyMapping: IFormBasePropertyMapping,
    formData: {}
  ): Observable<BaseModel> {
    model[propertyMapping.key] = formData[propertyMapping.key]
    return Observable.of(model)
  }

  private _nullModelStrategy(
    model: BaseModel,
    _propertyMapping: IFormBasePropertyMapping,
    _formData: {}
  ) {
    return Observable.of(model)
  }

  private _createPrompt(model: BaseModel, propertyMapping: IFormBasePropertyMapping) {
    const controlOptions = {}
    for (const property in propertyMapping) {
      controlOptions[property] = propertyMapping[property]
    }

    controlOptions['value'] = propertyMapping.valueCallback(model)
    return this._promptFactory.get(propertyMapping.controlType, controlOptions)
  }

  private _mergeDefaultPropertyMapping(propertyMapping: IFormBasePropertyMapping): IFormBasePropertyMapping {
    return Object.assign(
      { valueCallback: (model) => this._defaultValueCallback(model, propertyMapping.key) },
      propertyMapping
    )
  }

  private _defaultValueCallback(model: BaseModel, key: string): string {
    return model[key] || ''
  }

  private _defaultKeyIdentifier(model: BaseModel): string {
    return model.type
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/forms/services/form-mapper/form-mapper.ts